import { resolveCaseStudyClientLogo } from '@/lib/caseStudyClientLogo'

type Props = {
  client: string
  /** Same role as the year on home “Selected Designs” rows — regular weight, muted. */
  industry?: string
}

const compactIndustryLabel = (client: string, industry?: string): string => {
  if (/gosha\.ee/i.test(client)) {
    return 'AI-NATIVE DESIGN'
  }
  const normalized = (industry ?? '').replace(/[/,_-]+/g, ' ').trim()
  if (!normalized) {
    return ''
  }
  if (/united\s+accountants|^united$/i.test(client)) {
    return normalized
  }
  const words = normalized.split(/\s+/).filter(Boolean)
  return words.slice(0, 2).join(' ')
}

const clientInitials = (client: string): string => {
  const parts = client
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) {
    return 'CS'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

/** Black glyph on #B5FF12 (Selected Designs / Figma-aligned mark), paths from `clipflip.svg`. */
const ClipflipMark = () => (
  <span className="relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-[#B5FF12]">
    <svg className="size-[17px]" viewBox="0 0 100 100" aria-hidden>
      <path
        d="M67.6467 53.7813V64.7057H36.425C27.692 64.7057 20.5879 58.1091 20.5879 49.9998C20.5879 41.8906 27.692 35.2939 36.425 35.2939H79.4114V46.2183H39.3662C36.8775 46.2183 34.8413 47.6889 34.8413 49.9998C34.8413 52.3108 36.8775 53.7813 39.3662 53.7813H67.6467Z"
        fill="black"
      />
      <path d="M79.4116 58.8248L61.7646 69.0133L61.7646 48.6362L79.4116 58.8248Z" fill="black" />
    </svg>
  </span>
)

/** Static client row with same spacing/type as selected designs. */
export const CaseStudyClientLead = ({ client, industry }: Props) => {
  const logo = resolveCaseStudyClientLogo(client)
  const isClipflip = /clip\s*flip|clipflip\.io/i.test(client)
  const industryLabel = compactIndustryLabel(client, industry)

  return (
    <div aria-label={industryLabel ? `Client: ${client}, ${industryLabel}` : `Client: ${client}`}>
      <span className="relative z-10 flex flex-col gap-1 py-2.5 md:flex-row md:items-center md:justify-between md:gap-1">
        <span className="flex min-w-0 flex-row items-center gap-1.5">
          {isClipflip ? (
            <ClipflipMark />
          ) : logo ? (
            <span className="relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-[5px]">
              <img src={logo} alt={`${client} logo`} className="size-full object-contain" width={24} height={24} />
            </span>
          ) : (
            <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-[#202020] text-[10px] font-semibold leading-none text-white">
              {clientInitials(client)}
            </span>
          )}
          <span className="flex min-w-0 flex-row flex-wrap items-center gap-1 pb-0.5">
            <span className="font-display text-[16px] font-bold leading-5 tracking-[-0.16px] text-[var(--foreground)] uppercase">
              {client}
            </span>
            {industryLabel ? (
              <span className="font-display text-[16px] font-normal uppercase leading-5 tracking-[-0.16px] text-[var(--foreground-muted)]">
                {industryLabel}
              </span>
            ) : null}
          </span>
        </span>
      </span>
    </div>
  )
}
